/**
 * Abrechnungsmerkmale einer Krankenfahrt (`rides.medicalTransportBillingFacts`).
 *
 * Die Werte werden ausschließlich serverseitig aus dem rückwirkenden
 * Fahrertracking eingefroren (Cloud Function `freezeMedicalTransportBillingFacts`)
 * — das Portal liest sie nur. Hintergrund und Datenmodell:
 * docs/general/krankenfahrten_tarife_konzept.md (§5.3, §7).
 *
 * Wichtig: Krankenkassen rechnen nach Vergütungsvereinbarungen (§ 133 SGB V)
 * ab, nicht nach dem Taxitarif. Ein Betrag an der Fahrt ist deshalb nur so viel
 * wert wie seine Herkunft — daher überall der Herkunfts-Chip (§8.1).
 */

export const TRACK_QUALITY_LABELS = {
  ok: 'Verwertbar',
  review: 'Prüfen',
  unusable: 'Nicht verwertbar'
};

export const TRACK_QUALITY_COLORS = {
  ok: 'success',
  review: 'warning',
  unusable: 'error'
};

export const TRACK_QUALITY_FILTER_OPTIONS = [
  { value: 'all', label: 'Alle' },
  { value: 'ok', label: 'Verwertbar' },
  { value: 'review', label: 'Prüfen' },
  { value: 'unusable', label: 'Nicht verwertbar' },
  { value: 'missing', label: 'Noch nicht ermittelt' }
];

/** Preishierarchie §8.1 — welche Quelle steht hinter dem Betrag? */
export const FARE_SOURCE_LABELS = {
  payer_settled: 'lt. Kasse',
  contract_calc: 'Schätzung',
  taximeter_calibrated: 'Taxameter',
  contract_pending: 'offen'
};

export const FARE_SOURCE_COLORS = {
  payer_settled: 'success',
  contract_calc: 'info',
  taximeter_calibrated: 'default',
  contract_pending: 'default'
};

export const KM_SOURCE_LABELS = {
  gps_track: 'GPS',
  taximeter_calibrated: 'geeicht',
  route_api: 'Route',
  manual: 'manuell'
};

export const TRIP_DIRECTION_LABELS = {
  outbound: 'Hinfahrt',
  return: 'Rückfahrt',
  single: 'Einzelfahrt',
  // Die Verordnung deckt Hin- UND Rückfahrt ab, aber an der Fahrt steht nicht,
  // welche Hälfte sie war. Bewusst nicht als „Einzelfahrt" ausgewiesen — das
  // wäre ein Abrechnungsfehler statt einer Nachfrage.
  unknown: 'Richtung unklar'
};

export const COPAYMENT_LABELS = {
  unknown: 'Unbekannt',
  exempt: 'Befreit',
  required: 'Zuzahlungspflichtig',
  collected: 'Kassiert',
  not_collected: 'Nicht kassiert',
  collected_elsewhere: 'Anderweitig geklärt'
};

export const getBillingFacts = (ride) => {
  const facts = ride?.medicalTransportBillingFacts;
  return facts && typeof facts === 'object' ? facts : null;
};

export const getTrackVerdict = (ride) => {
  const facts = getBillingFacts(ride);
  return facts?.trackQuality?.verdict || null;
};

export const getOccupiedKm = (ride) => {
  const facts = getBillingFacts(ride);
  const value = facts?.occupiedKm;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

export const getDetourRatio = (ride) => {
  const facts = getBillingFacts(ride);
  const value = facts?.detourRatio;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/**
 * Herkunft des angezeigten Betrags. Solange kein Abrechnungszentrum den
 * bewerteten Betrag zurückmeldet, ist das bei Krankenfahrten `contract_pending`
 * — ein hier stehender `fare` ist dann eine Fahrer-Eingabe (Taxameter/Schätzung)
 * und ausdrücklich kein Kassenbetrag.
 */
export const getFareSource = (ride) => {
  const facts = getBillingFacts(ride);
  if (facts?.fareSource) return facts.fareSource;
  return null;
};

export const formatKm = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return `${number.toFixed(2).replace('.', ',')} km`;
};

export const formatRatio = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return `${(number * 100).toFixed(0)} %`;
};

/** Nur die Zahl, für CSV-Spalten (deutsches Dezimalkomma, ohne Einheit). */
export const csvNumber = (value, digits = 2) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return number.toFixed(digits).replace('.', ',');
};
